import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  deviceApi,
  SendWifiCredentialsRequest,
  UpdateDeviceStatusRequest,
} from '../../../services/deviceApi';
import { validateWifiCredentials } from './validation';
import { DevicesContext } from '../../../contexts/DevicesContext';
// import { isSecurePage } from '../../../utils/helperFunctions'; // re-enable with the HTTPS captive-portal path below
import {
  WebFlowCtx,
  WebStepKey,
  WEB_STEPS,
  WifiValidationErrors,
} from './types';

const enablePingCheck = process.env.REACT_APP_ENABLE_PING_CHECK === 'true';

const DEFAULT_OFFSETS = [
  { param: 'PM2.5', x: 1.0, y: 0.0 },
  { param: 'PM10', x: 1.0, y: 0.0 },
  { param: 'CO', x: 1.0, y: 0.0 },
  { param: 'SO₂', x: 1.0, y: 0.0 },
  { param: 'NO₂', x: 1.0, y: 0.0 },
  { param: 'O₃', x: 1.0, y: 0.0 },
  { param: 'CO₂', x: 1.0, y: 0.0 },
  { param: 'Humidity', x: 1.0, y: 0.0 },
  { param: 'Temperature', x: 1.0, y: 0.0 },
  { param: 'CH₂O', x: 1.0, y: 0.0 },
  { param: 'VOC', x: 1.0, y: 0.0 },
  { param: 'Noise', x: 1.0, y: 0.0 },
  { param: 'C₆H₆', x: 1.0, y: 0.0 },
];

interface UseWebFlowArgs {
  setShowConfigFlow: React.Dispatch<React.SetStateAction<boolean>>;
  SerialNumber: string;
  deviceId?: string;
}

interface PopupState {
  isOpen: boolean;
  status: 'success' | 'failed';
  title: string;
  subtitle: string;
  actionText?: string;
  onAction?: () => void;
  variant: 'default' | 'device';
  showAlertIcon?: boolean;
}

interface UseWebFlowResult {
  ctx: WebFlowCtx;
  popup: PopupState;
  confirmPopup: { isOpen: boolean };
  exitModalOpen: boolean;
  closePopup: () => void;
  closeConfirmPopup: () => void;
  closeExitModal: () => void;
  exitSetup: () => void;
}

export function useDeviceConfigFlowWeb({
  setShowConfigFlow,
  SerialNumber,
  deviceId,
}: UseWebFlowArgs): UseWebFlowResult {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const devicesContext = useContext(DevicesContext);

  const [step, setStep] = useState<WebStepKey>('on');
  const stepRef = useRef<WebStepKey>('on');
  useEffect(() => {
    stepRef.current = step;
  }, [step]);

  const [loading, setLoading] = useState(false);

  // Track whether this is a first-time onboarding (REGISTERED + UNKNOWN)
  // vs. re-onboarding an already-active device.
  const [isFirstOnboarding, setIsFirstOnboarding] = useState(true);
  const isFirstOnboardingRef = useRef(true);
  useEffect(() => {
    let cancelled = false;
    deviceApi.getDeviceBySerialNumber(SerialNumber).then((device) => {
      if (cancelled) return;
      const onboarding = (device.onboardingStatus ?? '').toUpperCase();
      const operational = (device.operationalStatus ?? '').toUpperCase();
      // First-time: device was just registered, Wi-Fi never configured
      const firstTime = onboarding === 'REGISTERED' && operational === 'UNKNOWN';
      isFirstOnboardingRef.current = firstTime;
      setIsFirstOnboarding(firstTime);
    }).catch(() => {
      // If fetch fails, default to first-time (safer — avoids overwriting ONLINE)
      isFirstOnboardingRef.current = true;
      setIsFirstOnboarding(true);
    });
    return () => { cancelled = true; };
  }, [SerialNumber]);

  // Wi-Fi & server form state
  const [wifiname, setWifiName] = useState('');
  const [wifipassword, setWifiPassword] = useState('');
  const [serverUrl, setServerUrl] = useState(
    'https://app.smarteco.ai/smartecodeviceapi',
  );
  const [validationErrors, setValidationErrors] =
    useState<WifiValidationErrors>({});

  // Modals
  const [confirmPopupIsOpen, setConfirmPopupIsOpen] = useState(false);
  const [exitModalOpen, setExitModalOpen] = useState(false);
  const [popup, setPopup] = useState<PopupState>({
    isOpen: false,
    status: 'success',
    title: '',
    subtitle: '',
    variant: 'default',
  });
  const popupOnActionRef = useRef<(() => void) | undefined>(undefined);

  /* ---------------- Popup helpers ---------------- */

  const closePopup = useCallback(() => {
    setPopup((p) => ({ ...p, isOpen: false }));
    popupOnActionRef.current = undefined;
  }, []);

  const showPopup = useCallback(
    (opts: Omit<PopupState, 'isOpen' | 'variant'> & { variant?: 'default' | 'device' }) => {
      popupOnActionRef.current = opts.onAction;
      setPopup({
        isOpen: true,
        status: opts.status,
        title: opts.title,
        subtitle: opts.subtitle,
        actionText: opts.actionText,
        onAction: opts.onAction,
        variant: opts.variant ?? 'default',
        showAlertIcon: opts.showAlertIcon,
      });
    },
    [],
  );

  /* ---------------- Navigation ---------------- */

  const goTo = useCallback((next: WebStepKey) => {
    setDeviceWifiError([]);
    setInternetError('');
    setStep(next);
  }, []);

  const goBack = useCallback(() => {
    const idx = WEB_STEPS.indexOf(stepRef.current);
    if (idx > 0) {
      setStep(WEB_STEPS[idx - 1]);
    }
  }, []);

  const onClose = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('model');
        return next;
      },
      { replace: true },
    );
    setShowConfigFlow(false);
  }, [setSearchParams, setShowConfigFlow]);

  const navigateToDashboard = useCallback(() => {
    // Trigger an immediate data refresh so the dashboard has fresh data
    // instead of waiting for the 5-minute polling interval.
    devicesContext?.getLiveDataForDashboard();
    onClose();
    navigate(deviceId ? `/devices/${deviceId}` : '/devices');
  }, [navigate, deviceId, onClose, devicesContext]);

  const navigateToAddDevice = useCallback(() => {
    onClose();
    navigate('/add-device');
  }, [navigate, onClose]);

  /* ---------------- URL ↔ step sync ---------------- */

  const setModelParam = useCallback(
    (model: WebStepKey, replace: boolean) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('model', model);
          return next;
        },
        { replace },
      );
    },
    [setSearchParams],
  );

  useEffect(() => {
    const view = searchParams.get('model') as WebStepKey | null;
    if (!view || !WEB_STEPS.includes(view)) {
      setModelParam('on', true);
      return;
    }
    if (view !== stepRef.current) {
      setStep(view);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    const view = searchParams.get('model');
    if (view === step) return;
    setModelParam(step, step === 'on' || step === 'done' || step === 'failed');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  /* ---------------- Exit confirmation ---------------- */

  const openExitModal = useCallback(() => setExitModalOpen(true), []);
  const closeExitModal = useCallback(() => setExitModalOpen(false), []);
  const exitSetup = useCallback(() => {
    setExitModalOpen(false);
    onClose();
  }, [onClose]);

  /* ---------------- Connectivity ---------------- */

  const checkInternet = useCallback(async (): Promise<boolean> => {
    if (!navigator.onLine) return false;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 3000);
    try {
      await fetch('https://www.google.com/generate_204', {
        method: 'GET',
        mode: 'no-cors',
        cache: 'no-store',
        signal: controller.signal,
      });
      clearTimeout(timer);
      return true;
    } catch {
      clearTimeout(timer);
      return false;
    }
  }, []);

  /* ---------------- Step 2: validate → confirm ---------------- */

  const verify = useCallback(() => {
    const errors = validateWifiCredentials(wifiname, wifipassword);
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) return;
    setConfirmPopupIsOpen(true);
  }, [wifiname, wifipassword]);

  const closeConfirmPopup = useCallback(() => setConfirmPopupIsOpen(false), []);

  const confirmWifiDetails = useCallback(() => {
    setConfirmPopupIsOpen(false);
    goTo('deviceWifi');
  }, [goTo]);

  /* ---------------- Step 3: verify laptop on device hotspot ---------------- */

  const [deviceWifiError, setDeviceWifiError] = useState<React.ReactNode[]>([]);
  const [internetError, setInternetError] = useState('');

  const connectToDeviceWifi = useCallback(async () => {
    setDeviceWifiError([]);
    setLoading(true);

    // When ping check is disabled, trust the user and advance immediately.
    if (!enablePingCheck) {
      setLoading(false);
      goTo('sendWifi');
      return;
    }

    // Ping the device's SoftAP endpoint to verify the user is actually
    // connected to the IAQ_ hotspot before advancing.
    const PING_TIMEOUT = 30000; // 30s
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), PING_TIMEOUT);

    try {
      const res = await fetch('http://192.168.4.1/ping', {
        method: 'GET',
        signal: controller.signal,
      });
      clearTimeout(timer);

      if (res.status === 200) {
        const json = await res.json();
        if (json?.status === 'ok') {
          setLoading(false);
          goTo('sendWifi');
          return;
        }
      }

      // Non-200 or unexpected response — treat as not connected.
      setLoading(false);
      setDeviceWifiError([
        'Make sure your computer is connected/reconnected to the \'IAQ_\' or  \'IAQ_' + SerialNumber +'\' Wi-Fi network.',
        React.createElement('span', null,
          'Verify that the Wi-Fi SSID and password you entered in ',
          React.createElement('a', {
            href: '#',
            style: { color: '#991B1B', textDecoration: 'underline', fontWeight: 600, cursor: 'pointer' },
            onClick: (e: React.MouseEvent) => { e.preventDefault(); goTo('wificonf'); },
          }, 'Step 2'),
          ' are correct.',
        ),
      ]);
    } catch {
      clearTimeout(timer);
      setLoading(false);
      setDeviceWifiError([
        'Open Settings \u2192 Wi-Fi and connect/reconnect to the \'IAQ_\' or  \'IAQ_' + SerialNumber +'\' Wi-Fi network.',
        React.createElement('span', null,
          'Double-check the Wi-Fi SSID and password you entered in ',
          React.createElement('a', {
            href: '#',
            style: { color: '#991B1B', textDecoration: 'underline', fontWeight: 600, cursor: 'pointer' },
            onClick: (e: React.MouseEvent) => { e.preventDefault(); goTo('wificonf'); },
          }, 'Step 2'),
          ' are correct.',
        ),
      ]);
    }
  }, [SerialNumber, goTo]);

  /* ---------------- Step 4: send Wi-Fi details to device ---------------- */

  const sendWifiDetails = useCallback(async () => {
    // ── HTTPS captive-portal path (disabled for now; re-enable when deploying
    // to HTTPS where the browser blocks POST to the device's http:// endpoint).
    // It opens the device's own setup page in a new tab (a top-level navigation
    // is allowed HTTPS→HTTP) with the creds as query params, then jumps to the
    // internet step. Keeping it here for later use.
    //
    // if (isSecurePage()) {
    //   const base = process.env.REACT_APP_DEVICE_PORTAL_URL || 'http://192.168.4.1';
    //   const params = new URLSearchParams({
    //     ssid: wifiname,
    //     password: wifipassword,
    //     serverUrl,
    //     deviceid: SerialNumber,
    //   });
    //   window.open(`${base}/?${params.toString()}`, '_blank', 'noopener');
    //   goTo('internet');
    //   return;
    // }

    setLoading(true);
    const payload: SendWifiCredentialsRequest = {
      ssid: wifiname,
      password: wifipassword,
      deviceid: SerialNumber,
      devicePassword: '',
      offsets: DEFAULT_OFFSETS,
      serverUrl,
      gateWayKey: '',
      enablebms: false,
      enablebacnet: false,
      enablemqtt: false,
      timestamp: Math.floor(Date.now() / 1000),
    };

    try {
      const result = await deviceApi.SendWifiCredentialsDirect(payload);
      setLoading(false);
      if (result.success) {
        goTo('internet');
      } else {
        setLoading(false);
        goTo('failed');
      }
    } catch (error) {
      setLoading(false);
      // eslint-disable-next-line no-console
      console.error('Error sending Wi-Fi data:', error);
      goTo('failed');
    }
  }, [wifiname, wifipassword, SerialNumber, serverUrl, goTo]);

  /* ---------------- Step 5: connect to internet + update backend ---------------- */

  const connectInternet = useCallback(async () => {
    setLoading(true);

    const online = await checkInternet();
    if (!online) {
      setLoading(false);
      setInternetError(
        'Your computer is not connected to the internet. Please reconnect to your normal Wi-Fi network, then click "I\'m Connected" again.',
      );
      return;
    }
    setInternetError('');

    const data: UpdateDeviceStatusRequest = {
      serialNumber: SerialNumber,
      onboardingStatus: 'WIFICONNECTED',
      operationalStatus: isFirstOnboardingRef.current ? 'INITIALIZE' : 'ONLINE',
      connectedWifiNetworkName: wifiname,
    };

    try {
      const response = await deviceApi.updateDeviceStatusBySerialNumber(data);
      setLoading(false);
      if (response?.success) {
        showPopup({
          status: 'success',
          title: 'Device Connected Successfully',
          subtitle:
            'Your SmartEco device is now monitoring your air quality.',
          actionText: 'OK',
          onAction: () => goTo('done'),
          variant: 'device',
        });
      } else {
        setLoading(false);
        goTo('failed');
      }
    } catch (error) {
      setLoading(false);
      // eslint-disable-next-line no-console
      console.error('Error updating device status:', error);
      goTo('failed');
    }
  }, [checkInternet, SerialNumber, wifiname, goTo, showPopup]);

  const ctx: WebFlowCtx = {
    step,
    goTo,
    goBack,
    serialNumber: SerialNumber,
    deviceId,
    wifiname,
    setWifiName,
    wifipassword,
    setWifiPassword,
    serverUrl,
    setServerUrl,
    validationErrors,
    loading,
    isFirstOnboarding,
    isSecurePage: false, // isSecurePage() — disabled for now (see sendWifiDetails)
    deviceWifiError,
    internetError,
    verify,
    confirmWifiDetails,
    connectToDeviceWifi,
    sendWifiDetails,
    connectInternet,
    navigateToDashboard,
    navigateToAddDevice,
    openExitModal,
  };

  return {
    ctx,
    popup: { ...popup, onAction: popupOnActionRef.current },
    confirmPopup: { isOpen: confirmPopupIsOpen },
    exitModalOpen,
    closePopup,
    closeConfirmPopup,
    closeExitModal,
    exitSetup,
  };
}
